import { useState, useEffect, useMemo } from "react";
import { fetchItems } from "../../utils/fetchItemData";
import { useModalStack } from "../../utils/modalContext";

const DURABILITY_ITEMS = [
  { id: "itm_axe_01", uses: 500, type: "Tool", notes: "" },
  { id: "itm_axe_02", uses: 1500, type: "Tool", notes: "" },
  { id: "itm_axe_03", uses: 4000, type: "Tool", notes: "" },
  { id: "itm_axe_04", uses: 7500, type: "Tool", notes: "" },
  { id: "itm_shears_01", uses: 500, type: "Tool", notes: "" },
  { id: "itm_shears_02", uses: 1500, type: "Tool", notes: "" },
  { id: "itm_shears_03", uses: 4000, type: "Tool", notes: "" },
  { id: "itm_shears_04", uses: 500, type: "Tool", notes: "" },
  { id: "itm_pickaxe_01", uses: 150, type: "Tool", notes: "" },
  { id: "itm_pickaxe_02", uses: 400, type: "Tool", notes: "" },
  { id: "itm_pickaxe_03", uses: 1250, type: "Tool", notes: "" },
  { id: "itm_pickaxe_04", uses: 2250, type: "Tool", notes: "" },
  { id: "itm_fishingNet", uses: 300, type: "Tool", notes: "" },
  { id: "itm_beginner_fishing_rod", uses: 300, type: "Tool", notes: "" },
  { id: "itm_newWateringCanLuke", uses: 60, type: "Special Tool", notes: "Waters a 3x3 Grid" },
  { id: "itm_sprinkler", uses: 168, type: "Special Tool", notes: "Waters a 5x5 grid every hour for 7 days (Uses don't reduce if you click it)" },
  { id: "itm_combineHarvest01", uses: 100, type: "Special Tool", notes: "Can harvest 5 rows of 10 columns. Place it at the first column you want to harvest in the center row of your target area." },
  { id: "itm_duraAxe_02", uses: 1500, type: "Upgraded Tool", notes: "Reduces how many chops it takes to cut a tree" },
  { id: "itm_duraAxe_03", uses: 4000, type: "Upgraded Tool", notes: "Reduces how many chops it takes to cut a tree" },
  { id: "itm_duraAxe_04", uses: 7500, type: "Upgraded Tool", notes: "Reduces how many chops it takes to cut a tree" },
  { id: "itm_duraPick_02", uses: 340, type: "Upgraded Tool", notes: "Reduces Mining Timer by 20%" },
  { id: "itm_duraPick_03", uses: 900, type: "Upgraded Tool", notes: "Reduces Mining Timer by 20%" },
  { id: "itm_duraPick_04", uses: 1690, type: "Upgraded Tool", notes: "Reduces Mining Timer by 20%" },
  { id: "itm_duraShears_02", uses: 300, type: "Upgraded Tool", notes: "Shears a 3x3 Grid of Crops (Only Crops of that tier and below)" },
  { id: "itm_duraShears_03", uses: 900, type: "Upgraded Tool", notes: "Shears a 3x3 Grid of Crops (Only Crops of that tier and below)" },
  { id: "itm_duraShears_04", uses: 1500, type: "Upgraded Tool", notes: "Shears a 3x3 Grid of Crops (Only Crops of that tier and below)" },
  { id: "itm_fishing_rod_2", uses: 2500, type: "Upgraded Tool", notes: "There's a 10% Chance to receive 2x Items" },
  { id: "itm_mini_farmamix", uses: 15, type: "Feed", notes: "Feeds Chickens & Cows" },
  { id: "itm_farmamix", uses: 100, type: "Feed", notes: "Feeds Cows, Turkeys, Chickens and Goats" },
  { id: "itm_apimix", uses: 50, type: "Feed", notes: "Feeds Bees & Slugs" },
  { id: "itm_algamix", uses: 30, type: "Feed", notes: "Feeds Ducks" },
  { id: "itm_mysticMix", uses: 30, type: "Feed", notes: "Feeds Dragons" },
  { id: "itm_choco_sauce", uses: 75, type: "Feed", notes: "Feeds Sheep & Pigs" },
  { id: "itm_gathering_satchel", uses: 180, type: "Tool", notes: "Gathers from all animals" },
];

const DurabilityGuide = () => {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("All");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "ascending" });
  const { pushModal } = useModalStack();

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        await fetchItems({ itemId: null, setItems });
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, []);

  // Item Modal
  const handleOpenModal = async (itemId) => {
    try {
      const data = await fetchItems({ itemId });
      if (data?.[0]) {
        pushModal({
          id: `item-${itemId}`,
          type: "itemModal",
          props: {
            item: data[0],
            onClose: () => {},
            showNavigation: false,
            onNavigate: null,
            totalItems: 1,
            index: null,
          },
        });
      }
    } catch (err) {
      console.error("Failed to fetch item:", err);
    }
  };

  // Merge durability data with fetched item data
  const durabilityItems = useMemo(() => {
    if (!items.length) return [];

    const itemMap = new Map(items.map((item) => [item.id, item]));

    return DURABILITY_ITEMS.map((dur) => {
      const item = itemMap.get(dur.id);
      if (!item) return null;
      return {
        id: item.id,
        image: item.image,
        name: item.name,
        uses: dur.uses,
        type: dur.type,
        notes: dur.notes,
        tier: item.tier,
      };
    }).filter(Boolean);
  }, [items]);

  // Get unique types from the items
  const typeOptions = useMemo(() => {
    const types = [...new Set(DURABILITY_ITEMS.map((item) => item.type))].sort();
    return ["All", ...types];
  }, []);
  const filteredItems = useMemo(() => {
    const filtered = durabilityItems.filter((item) => {
      const matchesSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesType = typeFilter === "All" || item.type === typeFilter;
      return matchesSearch && matchesType;
    });

    if (!sortConfig.key) return filtered;

    return [...filtered].sort((a, b) => {
      const valA = a[sortConfig.key];
      const valB = b[sortConfig.key];

      if (valA == null) return 1;
      if (valB == null) return -1;

      if (typeof valA === "string") {
        return sortConfig.direction === "ascending"
          ? valA.localeCompare(valB)
          : valB.localeCompare(valA);
      }

      return sortConfig.direction === "ascending" ? valA - valB : valB - valA;
    });
  }, [durabilityItems, searchQuery, sortConfig, typeFilter]);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "ascending" ? "descending" : "ascending",
    }));
  };

  const sortIcon = (key) => {
    if (sortConfig.key !== key) return "";
    return sortConfig.direction === "ascending" ? " ▲" : " ▼";
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="loading-spinner mb-3"></div>
          <div className="text-lg">Loading durability data...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="durability-container">
      <input
        type="text"
        placeholder="Search items..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        className="select-forms p-2 m-1"
      />

      <select
        value={typeFilter}
        onChange={(e) => setTypeFilter(e.target.value)}
        className="select-forms p-2 m-1"
      >
        {typeOptions.map((type) => (
          <option key={type} value={type}>{type}</option>
        ))}
      </select>

      <div className="max-h-[70vh] overflow-auto">
        <table className="market-table">
          <thead>
            <tr>
              <th className="cursor-pointer" onClick={() => handleSort("name")}>
                Item{sortIcon("name")}
              </th>
              <th className="cursor-pointer" onClick={() => handleSort("uses")}>
                Uses{sortIcon("uses")}
              </th>
              <th className="cursor-pointer" onClick={() => handleSort("type")}>
                Type{sortIcon("type")}
              </th>
              <th className="cursor-pointer" onClick={() => handleSort("tier")}>
                Tier{sortIcon("tier")}
              </th>
              <th className="cursor-pointer">
                Notes
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredItems.map((item) => (
              <tr key={item.id}>
                <td>
                  <div
                    className="inline-image-text cursor-pointer"
                    onClick={() => handleOpenModal(item.id)}
                  >
                    <img src={item.image} alt={item.name} width={24} height={24} className="inline" />
                    <span> {item.name}</span>
                  </div>
                </td>
                <td>{item.uses}</td>
                <td>{item.type}</td>
                <td>{item.tier ?? "N/A"}</td>
                <td>{item.notes || "-"}</td>
              </tr>
            ))}

            {filteredItems.length === 0 && (
              <tr>
                                  <td colSpan={5} className="text-center text-gray-500 p-4">
                  No items found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DurabilityGuide;